import { css } from '@emotion/react';
import styled from '@emotion/styled';
import tokens from '@vaa-component-lib/shared.design-tokens/json/variables.json';
import { BreakPointToken } from '../../hooks/useMediaQuery';
import { NoGapSupport } from '../../lib/utils/mixins';

export interface InputWidths {
	desktop: string;
	tablet: string;
}

export const {
	color: colours,
	spacing: {
		size_4px: { height: size_4 },
		size_8px: { height: size_8 },
		size_12px: { height: size_12 },
		size_16px: { height: size_16 },
		size_20px: { height: size_20 },
		size_24px: { height: size_24 },
		size_160px: { height: size_160 }
	}
} = tokens;

export const mobileGridGap = size_8;

export const tabletGridGaps = {
	horizontal: size_12,
	vertical: size_24 - 10
};

export const desktopGridGaps = {
	horizontal: size_8,
	vertical: size_24 - 10
};

export const submitBtnWidthDesktop = 177;

export const ServerError = styled.div`
	margin-bottom: ${size_16}px;
`;

export const FormGrid = styled.section`
	display: flex;
	flex-wrap: wrap;
	align-items: baseline;
	gap: ${mobileGridGap}px;

	@media (min-width: ${BreakPointToken.MdMin}px) {
		column-gap: ${tabletGridGaps.horizontal}px;
		row-gap: ${tabletGridGaps.vertical}px;
	}

	@media (min-width: ${BreakPointToken.LgMin}px) {
		column-gap: ${desktopGridGaps.horizontal}px;
		row-gap: ${desktopGridGaps.vertical}px;
	}
`;

export const column = css`
	${NoGapSupport(
		() => css`
			margin-top: ${mobileGridGap}px;

			@media (min-width: ${BreakPointToken.MdMin}px) {
				margin: 0;
			}
		`
	)}
`;

export const option = css`
	width: max-content;

	${NoGapSupport(
		() => css`
			margin-top: ${size_20}px;

			@media (min-width: ${BreakPointToken.MdMin}px) {
				margin-top: 0;
			}
		`
	)}
`;

export const rewardText = css`
	@media (min-width: ${BreakPointToken.MdMin}px) {
		margin-inline-end: auto;
		margin-inline-start: 4px;
		padding-inline-start: ${size_12}px;
		border-inline-start: 1px solid var(--token-color-colour-border-minimal);
	}
`;

export const half = css`
	flex: 0 0 calc(50% - ${mobileGridGap / 2}px);

	@media (min-width: ${BreakPointToken.MdMin}px) {
		flex: unset;
	}

	${NoGapSupport(
		() => css`
			& + & {
				margin-left: ${mobileGridGap}px;
				flex-shrink: 0;
				flex-grow: 0;
			}

			@media (min-width: ${BreakPointToken.MdMin}px) {
				flex: 0 0 auto;
				margin-right: ${tabletGridGaps.horizontal}px;

				& + & {
					margin-left: 0;
				}
			}
		`
	)}
`;

export const FormOptions = styled.section`
	display: contents;
	flex: 0 0 100%;

	@media (min-width: ${BreakPointToken.MdMin}px) {
		display: flex;
		gap: ${size_8}px;
		justify-content: space-between;
		align-items: center;
	}
`;

export const origin = (inputWidths: InputWidths) => css`
	@media (min-width: ${BreakPointToken.LgMin}px) {
		flex: 0 0 ${inputWidths.desktop};
	}
`;

export const destination = (inputWidths: InputWidths) => css`
	@media (min-width: ${BreakPointToken.LgMin}px) {
		flex: 0 0 ${inputWidths.desktop};
	}
`;

export const date = (inputWidths: InputWidths) => css`
	@media (min-width: ${BreakPointToken.MdMin}px) {
		flex: 0 0 ${inputWidths.tablet};
	}

	@media (min-width: ${BreakPointToken.LgMin}px) {
		flex: 0 0 ${inputWidths.desktop};
	}

	input {
		cursor: pointer;
	}
`;

export const passengers = (inputWidths: InputWidths) => css`
	@media (min-width: ${BreakPointToken.MdMin}px) {
		flex: 0 0 ${inputWidths.tablet};
	}

	@media (min-width: ${BreakPointToken.LgMin}px) {
		flex: 0 0 ${inputWidths.desktop};
	}

	input {
		cursor: pointer;
	}
`;

export const hotelInput = (inputWidths: InputWidths) => css`
	@media (min-width: ${BreakPointToken.MdMin}px) {
		flex: 0 0 ${inputWidths.tablet};
	}

	@media (min-width: ${BreakPointToken.LgMin}px) {
		flex: 0 0 ${inputWidths.desktop};
	}

	input {
		cursor: pointer;
	}
`;

export const submit = (inputWidths: InputWidths) => css`
	order: 5;
	margin-top: ${size_8}px;
	margin-left: auto;

	@media (min-width: ${BreakPointToken.MdMin}px) {
		flex: 0 0 ${inputWidths.tablet};
		order: unset;
		position: relative;
		top: -1px;
		margin-top: 0;
	}

	@media (min-width: ${BreakPointToken.LgMin}px) {
		flex: 0 0 ${submitBtnWidthDesktop}px;
	}

	button {
		width: 100%;
	}

	${NoGapSupport(
		() => css`
			@media (min-width: ${BreakPointToken.MdMin}px) {
				margin-left: ${tabletGridGaps.horizontal}px;
			}
		`
	)}
`;

export const hotelSubmit = (hotelInputWidths: InputWidths) => css`
	order: 5;

	@media (min-width: ${BreakPointToken.MdMin}px) {
		flex: 0 0 ${hotelInputWidths.tablet};
		order: unset;
	}

	@media (min-width: ${BreakPointToken.LgMin}px) {
		flex: 0 0 ${submitBtnWidthDesktop}px;
	}

	button {
		width: 100%;
	}

	${NoGapSupport(
		() => css`
			@media (min-width: ${BreakPointToken.MdMin}px) {
				margin-left: ${tabletGridGaps.horizontal}px;
			}
		`
	)}
`;

export const discount = css`
	order: 6;
	flex: 0 0 100%;
	margin: ${size_12}px 0;
	@media (min-width: ${BreakPointToken.MdMin}px) and (max-width: ${BreakPointToken.MdMax}px) {
		order: 8;
		flex: auto;
		justify-content: flex-end;
		margin: 0;
	}
	@media (min-width: ${BreakPointToken.LgMin}px) {
		order: 8;
		flex: 0 0 max-content;
		margin: 0;
	}
	text-align: center;
	display: flex;
	align-items: center;
	justify-content: center;
`;

// Shamefully changing the styles of readOnly inputs for Search XP
export const readOnly = css`
	& input {
		cursor: pointer;
	}

	& input[readonly] {
		&:not([disabled]) {
			border: 1px solid ${colours.light.border_accessible} !important;
			background-color: ${colours.light.background_primary} !important;
			transition: border-color 0.2s ease;

			& ~ label {
				background: ${colours.light.background_primary} !important;
			}

			& ~ div {
				--icon-color: ${colours.light.text_primary} !important;
			}

			&:hover,
			&:active {
				border: 1px solid ${colours.light.text_primary} !important;
			}

			&[class*='module_error'] {
				border: 1px solid ${colours.light.text_error} !important;

				&:hover,
				&:active {
					border: 1px solid ${colours.light.text_error} !important;
				}
			}
		}
	}
`;

export const Div = styled.div``;

export const LinkPanelGrid = styled.div`
	display: flex;
	flex-wrap: wrap;
`;

export const LinkPanelGridContent = styled.div`
	flex: 0 0 100%;
	@media (min-width: ${BreakPointToken.MdMin}px) {
		flex: 1;
	}
`;

export const LinkPanelGridAction = styled.div`
	flex: 0 0 100%;
	margin-top: ${size_16}px;
	@media (min-width: ${BreakPointToken.MdMin}px) {
		margin-left: auto;
		flex: unset;
		margin: unset;
	}
`;

export const BookingTypeMeta = styled.section`
	color: var(--token-color-colour-text-primary);
	margin-bottom: ${size_16}px;

	img {
		margin-bottom: ${size_16}px;
	}

	a {
		color: var(--token-color-colour-text-primary);
		text-decoration: underline;
	}
`;
